import React, {useContext, useEffect, useState} from 'react';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {ItemThumbPlaceholder} from '../../ItemPlaceholder/ItemThumbPlaceholder';
import {
    executeAction,
    formatDate,
    formatFiletype,
    formatSize,
    getFileExtension,
    isNullOrEmpty,
    isPreviewApplicable
} from '../../../utils';
import {config, dynamicConfig} from '../../../init/config';
import {LabelDTO} from '../../../models/dto/LabelDTO';
import {t} from 'i18next';
import {AuthContext} from '../../../context/AuthContext';
import {hasEditorialAccess, hasPublicOnlyAccess} from '../../../utils/UserUtils';
import {Label} from '../../../models/Label';
import {getLabel} from '../../../utils/StorageObjectUtils';
import {Button, CommandBar, MenuItem, Popover} from '@bosch/react-frok';
import {CommandActionWrapper, GridCustomButton, ItemCardTileWrapper, LabelImages} from './ItemGridTile.style';
import {LayoutViewType} from '../../../models/LayoutViewType';
import {isFileDownloadable} from '../../../utils/DtoUtils';
import {useGetThumbnailUrl} from '../../../hooks/UseGetThumbnailUrl';
import {generateIdentifier} from '../../../utils/IdentityUtils';

export interface ItemGridTileProps {
    readonly item: StorageObject;
    readonly language: string;
    readonly defaultLocale: string;
    readonly showEdit: boolean;
    readonly isFavoriteToggle: boolean | null;
    readonly labelData: LabelDTO[];
    onDeleteClicked: () => void;
    onDownloadClicked: () => void;
    onEditClicked: () => void;
    onDetailsClicked: () => void;
    onFavoriteClicked: () => void;
    onPreviewClicked: () => void;
    onCopyUrlClicked: () => void;
    onClick: () => void;
}

const wordBreakValueExtract = () => {
    if (config.DLS_WORD_BREAK) {
        return 'break-word'
    } else {
        return 'unset'
    }
};

/**
 * @name ItemGridTile
 * @description
 * The tile representing each folder/file on the main view as grid
 */
export const ItemGridTile: React.VFC<ItemGridTileProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isPublicOnlyAccess] = useState<boolean>(hasPublicOnlyAccess(isAuthenticated, roles));
    const [isEditorialAccess] = useState<boolean>(hasEditorialAccess(isAuthenticated, roles));
    const [label, setLabel] = useState<Label>(getLabel(props.defaultLocale, props.item.labels));
    const getThumbnailUrl = useGetThumbnailUrl();
    const [thumbnail, setThumbnail] = useState('');
    const setExistingThumbnail = async (thumbnailUrl: string) => {
        if (isNullOrEmpty(thumbnailUrl)) {
            return;
        }

        await getThumbnailUrl(thumbnailUrl)
            .then((existingThumbnail) => {
                if (!isNullOrEmpty(existingThumbnail)) {
                    setThumbnail(existingThumbnail);
                }
            })
            .catch();
    };

    useEffect(() => {
        if ((props.item.metadata.thumbnailUrl) && (!isNullOrEmpty(props.item.metadata.thumbnailUrl))) {
            setExistingThumbnail(props.item.metadata.thumbnailUrl).catch();
        }
    }, [props.item.metadata.thumbnailUrl]);

    useEffect(() => {
        setLabel(getLabel(props.defaultLocale, props.item.labels));
    }, [props.defaultLocale, props.item.labels]);

    let existingLabelFiltered: (LabelDTO | undefined)[] = [];
    if (dynamicConfig.LABEL_ENABLED) {
        const existingLabel = props.labelData.map(existingKey => {
            if (props.item.metadata.userLabels?.includes(existingKey.labelKey)) {
                return existingKey;
            }
        });
        existingLabelFiltered = existingLabel?.filter((Boolean));
    }

    const countDisplayedButtons = (item: StorageObject) => {
        let displayedButtons = 2;
        if (!isPublicOnlyAccess) {
            displayedButtons += 1;
        }
        if (props.showEdit) {
            displayedButtons += 2;
        }
        if (isPreviewApplicable(item.metadata.contentType, getFileExtension(item.metadata.blobUrl))) {
            displayedButtons += 1;
        }
        return displayedButtons;
    };

    const commandActions = () => {
        return (
            <CommandActionWrapper id={generateIdentifier()}>
                {props.showEdit && (
                    <div id={generateIdentifier()}>
                        <GridCustomButton id={generateIdentifier()}
                                          key='edit'
                                          icon='edit'
                                          mode='tertiary'
                                          type='button'
                                          title={t('GENERAL_EDIT_LABEL')}
                                          onClick={(e) => executeAction(e, props.onEditClicked)}/>
                        <GridCustomButton id={generateIdentifier()}
                                          key='delete'
                                          icon='delete'
                                          mode='tertiary'
                                          type='button'
                                          title={t('GENERAL_DELETE_LABEL')}
                                          onClick={(e) => executeAction(e, props.onDeleteClicked)}/>
                    </div>
                )}
                {(!isPublicOnlyAccess) &&
                <Button id={generateIdentifier()}
                        key='favorites'
                        icon={!!props.isFavoriteToggle ? 'wishlist-filled' : 'wishlist'}
                        mode='tertiary'
                        type='button'
                        title={t('GENERAL_FAVORITES_LABEL')}
                        onClick={(e) => executeAction(e, props.onFavoriteClicked)}/>
                }
                <GridCustomButton id={generateIdentifier()}
                                  key='view'
                                  icon='document'
                                  mode='tertiary'
                                  type='button'
                                  title={t('GENERAL_VIEW_LABEL')}
                                  onClick={(e) => executeAction(e, props.onDetailsClicked)}/>
                {((isPreviewApplicable(props.item.metadata.contentType, getFileExtension(props.item.metadata.blobUrl))) && (countDisplayedButtons(props.item) < 6)) &&
                <GridCustomButton id={generateIdentifier()}
                                  key='preview'
                                  icon='watch-on'
                                  mode='tertiary'
                                  type='button'
                                  title={t('GENERAL_PREVIEW_LABEL')}
                                  onClick={(e) => executeAction(e, props.onPreviewClicked)}/>
                }
                {(countDisplayedButtons(props.item) < 6) &&
                <GridCustomButton id={generateIdentifier()}
                                  key='copy-button'
                                  icon='copy'
                                  onClick={(e) => executeAction(e, props.onCopyUrlClicked)}
                                  mode='tertiary'
                                  type='button'
                                  title={t('GENERAL_COPY_URL_LABEL')}/>}
                {(countDisplayedButtons(props.item) >= 6) &&
                <Popover id={generateIdentifier(('popover-' + props.item.id, '-'))}
                         position='top-right'
                         onCloseButtonClick={() => document.body.click()}
                         onCloseKeyPressed={() => document.body.click()}
                         onOutsideClick={() => document.body.click()}
                         onTriggerClick={() => document.body.click()}
                         paragraph={
                             <div>
                                 <MenuItem id={generateIdentifier()}
                                           key='copy-menu-item'
                                           icon='copy'
                                           label='Copy Url'
                                           title={t('GENERAL_COPY_URL_LABEL')}
                                           onClick={(e) => {
                                               executeAction(e, props.onCopyUrlClicked);
                                               document.body.click();
                                           }}/>
                                 {(isPreviewApplicable(props.item.metadata.contentType, getFileExtension(props.item.metadata.blobUrl))) &&
                                 <MenuItem id={generateIdentifier()}
                                           key='preview-menu-item'
                                           icon='watch-on'
                                           label='Preview'
                                           title={t('GENERAL_PREVIEW_LABEL')}
                                           onClick={(e) => {
                                               executeAction(e, props.onPreviewClicked);
                                               document.body.click();
                                           }}/>
                                 }
                             </div>
                         }
                         trigger={
                             <Button id={generateIdentifier()}
                                     key='more'
                                     icon='options'
                                     mode='tertiary'
                                     type='button'
                                     title={t('GENERAL_OPTIONS_LABEL')}
                                     onClick={(e) => executeAction(e, () => {
                                     })}/>
                         }
                />}
            </CommandActionWrapper>
        );
    };

    return (
        <ItemCardTileWrapper id={generateIdentifier()}
                             className='a-tile -secondary'
                             onClick={(e) => executeAction(e, props.onClick)}>
            <div id={generateIdentifier()} className='item-thumbnail'>
                {!!(props.item.metadata.thumbnailUrl && (!isNullOrEmpty(props.item.metadata.thumbnailUrl))) ? (
                    <img id={generateIdentifier()}
                         className='-primary'
                         src={thumbnail}
                         alt={'thumbnail'}/>
                ) : (
                    <ItemThumbPlaceholder
                        contentType={(props.item.objectType === ObjectType.FOLDER) ? 'folder' : props.item.metadata.contentType ?? null}
                        viewType={LayoutViewType.GRID}/>
                )}
            </div>
            <div id={generateIdentifier()} className='a-text details-container'>
                <div id={generateIdentifier('item-details-row-first')} className='item-details-row-first'>
                    <span id={generateIdentifier()} className='-size-s'>
                        {formatDate(props.language, props.item.metadata.dateCreated)}
                    </span>
                    <span id={generateIdentifier()} className='-size-s'>
                        {' '} | {formatSize(props.item.totalSize)}
                    </span>
                    <span id={generateIdentifier()} className='-size-s'>
                        {' '} | {formatFiletype(props.item.objectType === ObjectType.FOLDER ? 'folder' : props.item.metadata.contentType ?? null)}
                    </span>
                </div>
                <div id={generateIdentifier('item-details-row-second')} className='item-details-row-second'>
                    <span id={generateIdentifier()} className='-size-s'>
                        {(props.item.metadata.tags && (props.item.metadata.tags.length > 0)) && props.item.metadata.tags.join(', ')}
                    </span>
                </div>
                <h3 id={generateIdentifier()} className='-size-xl item-title'>
                    <div id={generateIdentifier()}>
                        <div id={generateIdentifier()} style={{wordBreak: wordBreakValueExtract()}}>
                            {label.name}
                        </div>
                        <i id={generateIdentifier()} className='a-icon ui-ic-inline-right item-chevron'
                           title='inline-right'/>
                    </div>
                </h3>
                <div id={generateIdentifier('description-wrapper')} className='description-wrapper'
                     style={{wordBreak: wordBreakValueExtract()}}>
                    {isEditorialAccess &&
                    (props.item.objectType === ObjectType.FOLDER ?
                            <p id={generateIdentifier('item-description')} className='item-description -size-m'>
                                {t('FOLDER_DETAILS_PUBLIC_ACCESS', {isPublic: props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')})}
                            </p> :
                            <p id={generateIdentifier('item-description')} className='item-description -size-m'>
                                {t('FILE_DETAILS_PUBLIC_ACCESS', {isPublic: props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')})}
                            </p>
                    )}
                    <p id={generateIdentifier('item-description')}
                       className='item-description -size-m'>{label.description}</p>
                </div>
            </div>
            <div id={generateIdentifier('item-details-row-third')} className='item-details-row-third'>
                {(existingLabelFiltered && (existingLabelFiltered?.length > 0)) && existingLabelFiltered?.map(labelDtoObj => {
                    return <div id={generateIdentifier('item-details-label-column')}
                                className='item-details-label-column'
                                key={labelDtoObj?.labelKey}>
                        <LabelImages id={generateIdentifier()} src={labelDtoObj?.label} alt={'label'}/>
                    </div>
                })}
            </div>
            <CommandBar id={generateIdentifier()}
                        className='item-menu'
                        actions={[]}
                        content={commandActions()}
                        mainAction={
                            (props.item.objectType === ObjectType.FILE) && (isFileDownloadable(props.item.status)) && (
                                <Button id={generateIdentifier()}
                                        key='download'
                                        icon='download'
                                        mode='secondary'
                                        type='button'
                                        aria-label={t('GENERAL_DOWNLOAD_LABEL')}
                                        title={t('GENERAL_DOWNLOAD_LABEL')}
                                        onClick={(e) => executeAction(e, props.onDownloadClicked)}/>
                            )
                        }
            />
        </ItemCardTileWrapper>
    );
};
